// ts-rest contract for the Benchmarking module (SEP-199) — each client's
// performance against the ARC peer benchmark the Tercero export names.
//
// ONE ENDPOINT SERVES THREE FRONTENDS. Desk, Ops and View all render the same
// page. The rows a caller gets back differ, because the server scopes them, and
// `canFilterByManager` tells the page whether to offer the Manager filter. The
// page never decides its own scope.
//
// THE SERVER SENDS ROWS; THE PAGE FILTERS THEM. Filtering, the global search,
// sorting and the chart averages all run in the browser over the rows below.
// That is deliberate: SEP-199 requires the chart to track the search box as
// well as every filter, and one filtered set in one place is the only way the
// table and the chart cannot disagree. The dataset is one row per account for
// one month, which is small enough to send whole.
//
// The server still owns everything that is a domain rule: which month is
// current, which rows the caller may see, which ARC band a label maps to, the
// eight peer-benchmark returns, and the subtraction.
//
// UNITS. Every number here is a PERCENT: 8.2 means 8.20 percent. The export's
// return cells are percents, `periodReturnPct` returns a percent, and the
// relative figure is one percent less another. A fraction anywhere in this
// chain gives an answer wrong by 100 times that still looks plausible.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** The eight periods the page shows, in display order (SEP-199). */
export const PEER_BENCHMARK_PERIODS = ['1m', '3m', '6m', '1y', '2y', '3y', '5y', 'ytd'] as const;
export const peerBenchmarkPeriodSchema = z.enum(PEER_BENCHMARK_PERIODS);
export type PeerBenchmarkPeriod = z.infer<typeof peerBenchmarkPeriodSchema>;

/** The column header for each period. */
export const PEER_BENCHMARK_PERIOD_LABELS: Readonly<Record<PeerBenchmarkPeriod, string>> = {
    '1m': '1M',
    '3m': '3M',
    '6m': '6M',
    '1y': '1Y',
    '2y': '2Y',
    '3y': '3Y',
    '5y': '5Y',
    ytd: 'YTD',
};

/** The two monthly upload slots. Provenance: no surface shows the book. */
export const PEER_BENCHMARK_BOOKS = ['dc', 'dgw'] as const;
export const peerBenchmarkBookSchema = z.enum(PEER_BENCHMARK_BOOKS);
export type PeerBenchmarkBook = z.infer<typeof peerBenchmarkBookSchema>;

/**
 * A reporting month, `YYYY-MM`.
 *
 * The month part must be a real month. A plain `\d{2}` admits `2026-13` and
 * `2026-00`, and such a typo sorts as the newest month, so the page then opens
 * on it for every reader. The year has no upper bound on purpose: an operator
 * can load a month early, and a refusal from the clock is a support burden.
 */
export const peerBenchmarkMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

/**
 * Percent return for each period. Null means the figure cannot be shown, which
 * the page renders as a dash and never as zero. On the client side that means
 * the account lacks that much history; on the peer side it means the ARC series
 * does not reach back to the period's anchor.
 */
export const peerBenchmarkPeriodValuesSchema = z.object({
    '1m': z.number().nullable(),
    '3m': z.number().nullable(),
    '6m': z.number().nullable(),
    '1y': z.number().nullable(),
    '2y': z.number().nullable(),
    '3y': z.number().nullable(),
    '5y': z.number().nullable(),
    ytd: z.number().nullable(),
});
export type PeerBenchmarkPeriodValues = z.infer<typeof peerBenchmarkPeriodValuesSchema>;

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * The three permitted reporting categories, restated here for the wire. The
 * domain source is `KPI_CLIENT_CATEGORIES` in `@septima/ontology-wealth`
 * (spec 6, Appendix A), and the server folds each raw export string into one of
 * these with `resolveClientCategory`. The contract package does not depend on
 * the ontology packages, so the values are written out, as the other contracts
 * that mirror a closed ontology list do.
 */
export const peerBenchmarkCategoryGroupSchema = z.enum([
    'Advisory',
    'Discretionary & Advisory Managed',
    'Execution Only',
]);
export type PeerBenchmarkCategoryGroup = z.infer<typeof peerBenchmarkCategoryGroupSchema>;

/** The filter's option list, in the order the page offers them. */
export const PEER_BENCHMARK_CATEGORY_GROUPS = peerBenchmarkCategoryGroupSchema.options;

export const peerBenchmarkRowSchema = z.object({
    /** Stable per-row key for the table. */
    id: z.string(),
    /** The export's own account key, shown as "Client Reference". */
    clientReference: z.string().nullable(),
    clientName: z.string().nullable(),
    /** The raw exported category, for example "DC - Advisory - High". */
    clientCategory: z.string().nullable(),
    /**
     * The raw category folded into the three permitted reporting values
     * (Advisory / Discretionary & Advisory Managed / Execution Only), through
     * the same Appendix A mapping the KPI Reporting page uses. Null when the
     * raw string resolves to none of them.
     *
     * The page filters on THIS, not on `clientCategory`: the raw column holds
     * roughly eighty spellings that fuse a service type with a risk band, and a
     * filter listing all of them asked the reader to scroll a page to make one
     * choice. The table still SHOWS the raw value, because that is the string
     * the export carries and the one an operator checks against Tercero.
     */
    clientCategoryGroup: peerBenchmarkCategoryGroupSchema.nullable(),
    /** Null on a Desk-scoped read, where every row has the same manager. */
    managerName: z.string().nullable(),
    /** The raw exported label, for example "ARC Sterling Balanced Asset PCI". */
    peerBenchmarkLabel: z.string().nullable(),
    /**
     * A fact about the LABEL: did the exported string resolve to an ARC index?
     *
     * False only when the label is blank or when it names a benchmark the ARC
     * band map does not recognise. It says nothing about the reporting month.
     * The page shows a dash in the Benchmark Name cell for such a row, and the
     * Benchmark Name filter offers no option for it.
     */
    peerBenchmarkLabelMatched: z.boolean(),
    /**
     * A fact about the COMPARISON: can a peer figure be shown for this row in
     * this month?
     *
     * It needs BOTH a matched label AND an ARC series that covers the reporting
     * month. The month half is identical for every row, so in the window after
     * the returns exports land and before ARC publishes the month, every row is
     * false while `peerBenchmarkLabelMatched` stays true. The page must not read
     * this flag as "the label is bad": use `peerBenchmarkLabelMatched` for that.
     *
     * A false row STILL appears in the table, with a dash in every peer and
     * relative column, and it is excluded from the chart averages so both series
     * count the same accounts (SEP-199).
     */
    peerBenchmarkMatched: z.boolean(),
    /** The account's own returns, from the export. */
    client: peerBenchmarkPeriodValuesSchema,
    /** The ARC index returns for this account's band. All null when unmatched. */
    peer: peerBenchmarkPeriodValuesSchema,
    /** `client` less `peer`. Positive is outperformance. */
    relative: peerBenchmarkPeriodValuesSchema,
});
export type PeerBenchmarkRow = z.infer<typeof peerBenchmarkRowSchema>;

/** One selectable reporting month, and which of the two books have landed. */
export const peerBenchmarkMonthOptionSchema = z.object({
    month: peerBenchmarkMonthSchema,
    books: z.array(peerBenchmarkBookSchema),
});
export type PeerBenchmarkMonthOption = z.infer<typeof peerBenchmarkMonthOptionSchema>;

export const peerBenchmarkingResponseSchema = z.object({
    /** The month these rows describe. Null before any upload. */
    month: peerBenchmarkMonthSchema.nullable(),
    /** Every stored month, latest first. The page defaults to the first. */
    availableMonths: z.array(peerBenchmarkMonthOptionSchema),
    /**
     * Can the peer-benchmark side be shown at all for this month?
     *
     * False when no ARC workbook has been uploaded, or when the ARC series
     * carries no level inside the reporting month. In the second case the
     * server refuses rather than measure the peer return over an earlier
     * window than the client return.
     */
    peerBenchmarkAvailable: z.boolean(),
    /** Is the ARC level for this month still ARC's provisional estimate? */
    peerBenchmarkIsEstimate: z.boolean(),
    /**
     * May the page offer the Manager filter? False for a Desk-scoped reader,
     * whose rows all carry one manager. The page must not infer this from the
     * rows: an empty month would then hide the filter for everyone.
     */
    canFilterByManager: z.boolean(),
    /**
     * Is this reader scoped to their own book? True on the Desk frontend for an
     * investment manager. The SAME person reading from Ops or View gets false
     * and the whole firm, because the active persona decides (SEP-199).
     */
    deskScoped: z.boolean(),
    /**
     * Has the FIRM any benchmarking data at all, whatever this caller can see?
     *
     * `availableMonths` is scoped to the caller, so a Desk reader whose
     * exported manager name did not resolve sees no month and would be told
     * that nothing is uploaded. That sends them after the wrong fault. This
     * flag separates "nothing is uploaded" from "nothing is yours", so the page
     * can name the manager mapping instead.
     *
     * Safe to send to every persona: it is one boolean about the firm. It
     * carries no client, no manager and no count, so it widens no read.
     */
    firmHasData: z.boolean(),
    rows: z.array(peerBenchmarkRowSchema),
});
export type PeerBenchmarkingResponse = z.infer<typeof peerBenchmarkingResponseSchema>;

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

/**
 * What one returns upload did. `unmatchedManagers` matters: a row whose manager
 * did not resolve is invisible to each Desk reader, so the operator must be
 * told rather than left to find the gap on the page.
 */
export const peerBenchmarkUploadResponseSchema = z.object({
    reportingMonth: peerBenchmarkMonthSchema,
    book: peerBenchmarkBookSchema,
    rowsParsed: z.number().int().nonnegative(),
    rowsWritten: z.number().int().nonnegative(),
    rowsReplaced: z.number().int().nonnegative(),
    duplicatesCollapsed: z.number().int().nonnegative(),
    ownerMatched: z.number().int().nonnegative(),
    unmatchedManagers: z.array(z.string()),
    /** True when the file matched the previous upload for this month exactly. */
    duplicateOfPrevious: z.boolean(),
});
export type PeerBenchmarkUploadResponse = z.infer<typeof peerBenchmarkUploadResponseSchema>;

/** What one ARC PCI workbook upload replaced. */
export const peerIndexUploadResponseSchema = z.object({
    indices: z.number().int().nonnegative(),
    levels: z.number().int().nonnegative(),
    /** The latest month the refreshed series reaches, `YYYY-MM`, or null. */
    latestMonth: z.string().nullable(),
});
export type PeerIndexUploadResponse = z.infer<typeof peerIndexUploadResponseSchema>;

// ---------------------------------------------------------------------------

export const peerBenchmarkingContract = c.router(
    {
        /**
         * Every row the caller may see for one reporting month, with the client
         * returns, the peer-benchmark returns and the difference.
         *
         * `month` is optional. Absent means the latest stored month, which is
         * what the page loads by default.
         */
        get: {
            method: 'GET',
            path: '/peer-benchmarking',
            query: z.object({ month: peerBenchmarkMonthSchema.optional() }).optional(),
            responses: { 200: peerBenchmarkingResponseSchema, 403: errorBody },
            summary: 'Client performance against the ARC peer benchmark for one reporting month.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
